import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useThemeMode } from '@/contexts/ThemeModeContext';

const RAIL_COLLAPSE_DELAY_MS = 540;
const ICON_CENTER_DELAY_MS = 420;

function TypingText({ text, active, delay = 120, className = '' }) {
    const [progress, setProgress] = useState(active ? 0 : 1);
    const progressRef = useRef(active ? 0 : 1);
    const visibleCharacterCount = Math.max(0, Math.ceil(text.length * progress));
    const deletingText = text.slice(0, visibleCharacterCount);

    useEffect(() => {
        const targetProgress = active ? 1 : 0;

        if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            progressRef.current = targetProgress;
            setProgress(targetProgress);
            return;
        }

        const startProgress = progressRef.current;
        if (startProgress === targetProgress) {
            return;
        }

        let frameId;
        let startTime;
        const duration = active
            ? Math.max(360, Math.min(820, text.length * 48))
            : Math.max(320, Math.min(560, text.length * 34));

        const easeInOutCubic = (value) => (
            value < 0.5
                ? 4 * value * value * value
                : 1 - Math.pow(-2 * value + 2, 3) / 2
        );

        const animate = (timestamp) => {
            if (startTime === undefined) {
                startTime = timestamp;
            }

            const elapsed = timestamp - startTime;
            const animationProgress = Math.min(elapsed / duration, 1);
            const easedProgress = easeInOutCubic(animationProgress);
            const nextProgress = startProgress + ((targetProgress - startProgress) * easedProgress);

            progressRef.current = nextProgress;
            setProgress(nextProgress);

            if (animationProgress < 1) {
                frameId = window.requestAnimationFrame(animate);
                return;
            }
        };

        const timeoutId = window.setTimeout(() => {
            frameId = window.requestAnimationFrame(animate);
        }, active ? delay : 0);

        return () => {
            window.clearTimeout(timeoutId);
            if (frameId) {
                window.cancelAnimationFrame(frameId);
            }
        };
    }, [active, delay, text]);

    return (
        <span className={`relative inline-grid align-middle ${className}`}>
            <span className="invisible whitespace-nowrap" aria-hidden="true">
                {text}
            </span>
            {active ? (
                <span
                    className="absolute inset-y-0 left-0 overflow-hidden whitespace-nowrap"
                    style={{ width: `${progress * 100}%` }}
                >
                    <span className="inline-block whitespace-nowrap">
                        {text}
                    </span>
                </span>
            ) : (
                <span className="absolute inset-y-0 left-0 whitespace-nowrap">
                    {deletingText}
                </span>
            )}
        </span>
    );
}

export default function Navbar({ currentPath, menuItems, onNavigate, collapsed = false }) {
    const { resolvedMode } = useThemeMode();
    const [railCollapsed, setRailCollapsed] = useState(collapsed);
    const [iconsCentered, setIconsCentered] = useState(collapsed);

    useEffect(() => {
        if (!collapsed) {
            setRailCollapsed(false);
            setIconsCentered(false);
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setRailCollapsed(true);
        }, RAIL_COLLAPSE_DELAY_MS);

        return () => window.clearTimeout(timeoutId);
    }, [collapsed]);

    useEffect(() => {
        if (!railCollapsed) {
            setIconsCentered(false);
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setIconsCentered(true);
        }, ICON_CENTER_DELAY_MS);

        return () => window.clearTimeout(timeoutId);
    }, [railCollapsed]);

    const navClassName = resolvedMode === 'dark'
        ? 'border-r app-shell-glass backdrop-blur-md shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]'
        : 'backdrop-blur-md';

    return (
        <aside className={`fixed inset-0 z-40 flex h-dvh w-full flex-col overflow-x-hidden bg-wave pt-14 transition-[width] duration-500 ease-in-out lg:sticky lg:inset-y-14 lg:h-[calc(100vh-3.5rem)] lg:pt-0 ${railCollapsed ? 'lg:w-20' : 'lg:w-65'}`}>
            {/* Frosted Glass Overlay */}
            <nav className={`z-30 flex h-full w-full flex-col justify-between gap-4 p-2 transition-[padding] duration-500 ease-in-out ${railCollapsed ? 'lg:px-2 lg:py-6' : 'lg:px-3 lg:py-6'} ${navClassName}`}>
                <ul className="space-y-2">
                    {menuItems.map((item, index) => {
                        return (
                            <li key={item.path || item.label}>
                                {item.children ? (
                                    <NavSection
                                        currentPath={currentPath}
                                        item={item}
                                        onNavigate={onNavigate}
                                        resolvedMode={resolvedMode}
                                        collapsed={collapsed}
                                        railCollapsed={railCollapsed}
                                        iconsCentered={iconsCentered}
                                        delay={140 + (index * 80)}
                                    />
                                ) : (
                                    <NavLink
                                        currentPath={currentPath}
                                        item={item}
                                        onNavigate={onNavigate}
                                        resolvedMode={resolvedMode}
                                        collapsed={collapsed}
                                        railCollapsed={railCollapsed}
                                        iconsCentered={iconsCentered}
                                        delay={140 + (index * 80)}
                                    />
                                )}
                            </li>
                        );
                    })}
                </ul>
                <div className="overflow-hidden px-3">
                    <div className={`flex items-center gap-4 whitespace-nowrap transition-[max-width,opacity,transform] ease-out ${railCollapsed ? 'max-w-0 -translate-x-2 opacity-0 duration-300' : 'max-w-xs translate-x-0 opacity-100 delay-150 duration-500'}`}>
                        <Image
                            src="/icons/nhazca.png"
                            alt="NHAZCA logo"
                            width={112}
                            height={40}
                            className="w-28 drop-shadow-md"
                        />
                        <Image
                            src="/icons/esa-bw.png"
                            alt="ESA logo"
                            width={64}
                            height={40}
                            className="w-16 drop-shadow-md"
                        />
                    </div>
                </div>
            </nav >
        </aside >
    );
}

function NavSection({ currentPath, item, onNavigate, resolvedMode, collapsed, railCollapsed, iconsCentered, delay }) {
    return (
        <div className="space-y-2">
            <div className="px-2 py-2">
                <div className="flex items-center gap-2 overflow-hidden">
                    <span className="h-px flex-1 bg-white/25" />
                    <p
                        className={`overflow-hidden whitespace-nowrap text-center text-xs font-bold uppercase tracking-[0.16em] text-white/90 text-shadow-sm text-shadow-gray-800 transition-[max-width,opacity,transform] ease-out ${railCollapsed ? 'max-w-0 -translate-x-2 opacity-0 duration-280' : 'max-w-40 translate-x-0 opacity-100 duration-300'}`}
                        aria-hidden={collapsed}
                    >
                        <TypingText
                            text={item.label}
                            active={!collapsed}
                            delay={delay}
                        />
                    </p>
                    <span className="h-px flex-1 bg-white/25" />
                </div>
            </div>
            <div className="space-y-2">
                {item.children.map((child) => (
                    <NavLink
                        key={child.path}
                        currentPath={currentPath}
                        item={child}
                        onNavigate={onNavigate}
                        resolvedMode={resolvedMode}
                        collapsed={collapsed}
                        railCollapsed={railCollapsed}
                        iconsCentered={iconsCentered}
                        delay={delay + 60}
                    />
                ))}
            </div>
        </div>
    );
}

function NavLink({ currentPath, item, onNavigate, resolvedMode, collapsed, railCollapsed, iconsCentered, delay = 160 }) {
    const isActive = currentPath === item.path;
    const linkClassName = resolvedMode === 'dark'
        ? isActive
            ? 'bg-white/10 text-white shadow-md inset-shadow-sm inset-shadow-white/10'
            : 'text-white hover:bg-white/10'
        : isActive
            ? 'bg-white/10 text-black shadow-md inset-shadow-sm inset-shadow-white/20'
            : 'text-black hover:bg-white/10';

    return (
        <Link
            href={item.path}
            onClick={onNavigate}
            className={`flex items-center overflow-hidden rounded-lg py-3 transition-[background-color,box-shadow,gap,padding,justify-content] duration-320 ease-in-out ${iconsCentered ? 'justify-center gap-0 px-2' : 'gap-3 px-3'} ${linkClassName}`}
            title={collapsed ? item.label : undefined}
            aria-label={item.label}
        >
            <div
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${isActive
                    ? 'bg-teal-900'
                    : 'bg-teal-800'
                    }`
                }
            >
                <span className="material-icons text-white text-2xl!">{item.icon}
                </span>
            </div>
            <span
                className={`overflow-hidden whitespace-nowrap font-bold text-white text-shadow-sm text-shadow-gray-800 transition-[max-width,opacity,transform] ease-out ${railCollapsed ? 'max-w-0 translate-x-1 opacity-0 duration-280' : 'max-w-40 translate-x-0 opacity-100 duration-300'}`}
                aria-hidden={collapsed}
            >
                <TypingText
                    text={item.label}
                    active={!collapsed}
                    delay={delay}
                />
            </span>
        </Link>
    );
}
